import React, { useRef, useState, useEffect, useCallback } from 'react';
import { FaHeart, FaComment, FaShare } from 'react-icons/fa';
import {  FaPaperPlane } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import PropertyCard from '../../Components/PropertyCard/PropertyCard';
import api from '../../api/axiosConfig';
import CommentPanel from '../../Components/PropertyCard/CommentPanel';
import ShareModal from '../../Components/PropertyCard/ShareModal';
import PropertyModal from '../../Components/PropertyCard/PropertyModal';
import { useTranslation } from 'react-i18next';
import { stringToColor } from '../../utils/avatarColor';

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="relative">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-200"></div>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent absolute top-0 left-0"></div>
      </div>
    </div>
  );
}

// Optimized Image component for action buttons
const OptimizedImage = React.memo(({ src, alt, className, loading = "lazy", t }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
  }, []);

  if (imageError) {
    return (
      <div className={`bg-gray-200 dark:bg-gray-700 flex items-center justify-center ${className}`}>
        <span className="text-gray-400">{t('home.imageError')}</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-500 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        loading={loading}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
});

// Custom Currency Selector Component
function CurrencySelector({ userCurrency, setUserCurrency, fxRates, localStorage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(userCurrency);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedCurrency(userCurrency);
  }, [userCurrency]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (currency) => {
    setSelectedCurrency(currency);
    setUserCurrency(currency);
    localStorage.setItem('preferredCurrency', currency);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="px-3 py-2 bg-[var(--bg-black)] text-[var(--text-black)] border border-white/30 rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-white/50 shadow-md hover:shadow-lg transition-shadow duration-200 flex items-center justify-between min-w-[80px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedCurrency || 'Currency'}
        <svg 
          className={`ml-2 w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-full bg-[var(--bg-black)] border border-white/30 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {Object.keys(fxRates).map((currency) => (
            <div
              key={currency}
              className={`px-3 py-2 text-xs cursor-pointer hover:bg-pink-500/30 transition-colors duration-150 ${
                selectedCurrency === currency ? 'bg-pink-500/50' : ''
              }`}
              onClick={() => handleSelect(currency)}
            >
              {currency}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Custom Language Selector Component
function LanguageSelector({ i18n, localStorage }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(i18n.language);
  const dropdownRef = useRef(null);

  const languages = [
    { code: "kn", name: "Kinyarwanda" },
    { code: "sw", name: "Swahili" },
    { code: "en", name: "English" },
    { code: "lg", name: "Luganda" },
    { code: "zu", name: "Zulu" },
    { code: "fr", name: "Français" },
    { code: "ar", name: "العربية" },
    { code: "hi", name: "हिन्दी" },
    { code: "zh-Hant", name: "中文 (Traditional)" },
    { code: "zh-Hans", name: "中文 (Simplified)" },
    { code: "ak", name: "Akan" },
    { code: "am", name: "Amharic" },
    { code: "ay", name: "Aymara" },
    { code: "bm", name: "Bambara" },
    { code: "be", name: "Bemba" },
    { code: "bn", name: "Bengali" },
    { code: "ber", name: "Berber" },
    { code: "nl", name: "Dutch" },
    { code: "de", name: "German" }, 
    { code: "gik", name: "Gikuyu" },
    { code: "gr", name: "Greek" }, 
    { code: "es", name: "Spanish" },
    { code: "fi", name: "Finnish" },
    { code: "ha", name: "Hausa" },
    { code: "ig", name: "Igbo" },
    { code: "id", name: "Indonesian" },
    { code: "it", name: "Italian" },
    { code: "ja", name: "Japanese" },
    { code: "km", name: "Khmer" },
    { code: "ki", name: "Kikuyu" },
    { code: "ko", name: "Korean" },
    { code: "ht", name: "Haitian Creole" },
    { code: "mg", name: "Malagasy" },
    { code: "ml", name: "Malayalam" },
    { code: "mr", name: "Marathi" },
    { code: "ne", name: "Nepali" },
    { code: "om", name: "Oromo" },
    { code: "ps", name: "Pashto" },
    { code: "pl", name: "Polish" },
    { code: "pt", name: "Portuguese" },
    { code: "pa", name: "Punjabi" },
    { code: "qu", name: "Quechua" },
    { code: "ru", name: "Russian" },
    { code: "so", name: "Somali" },
    { code: "sq", name: "Albanian" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "th", name: "Thai" },
    { code: "ti", name: "Tigrinya" },
    { code: "tr", name: "Turkish" },
    { code: "ur", name: "Urdu" },
    { code: "vi", name: "Vietnamese" },
    { code: "wo", name: "Wolof" },
    { code: "xh", name: "Xhosa" },
    { code: "yo", name: "Yoruba" },
    { code: "zu", name: "Zulu" }
  ];

  const selectedLanguage = languages.find(lang => lang.code === selectedLang);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (code, name) => {
    setSelectedLang(code);
    i18n.changeLanguage(code);
    localStorage.setItem('language', code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className="px-3 py-2 bg-pink-500 text-white border border-white/30 rounded-full text-xs focus:outline-none focus:ring-1 focus:ring-white/50 shadow-md hover:shadow-lg transition-shadow duration-200 flex items-center justify-between min-w-[100px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        {selectedLanguage?.name || 'Language'}
        <svg 
          className={`ml-2 w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-full bg-[var(--bg-black)] border border-white/30 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {languages.map((lang) => (
            <div
              key={lang.code}
              className={`px-3 py-2 text-xs cursor-pointer hover:bg-pink-500/30 transition-colors duration-150 ${
                selectedLang === lang.code ? 'bg-pink-500/50' : ''
              }`}
              onClick={() => handleSelect(lang.code, lang.name)}
            >
              {lang.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Comment Modal (vertical pop up)
export default function HomeDash() {
  const containerRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef(null);
  const location = useLocation()
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentPanel, setCommentPanel] = useState({ isOpen: false, propertyId: null, comments: [] });
  const [shareModal, setShareModal] = useState({ isOpen: false, property: null });
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [likeBusyIds, setLikeBusyIds] = useState(new Set());
  const [commentBusyId, setCommentBusyId] = useState(null);


  const { t, i18n } = useTranslation('Homedash');
  const [userCurrency, setUserCurrency] = useState('RWF');
  const [fxRates, setFxRates] = useState({});

  // Add Scroll-Based Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  

  // FETCH INITIAL + NEXT PAGES
  const fetchFeedPage = useCallback(async (page) => {
    if (!hasMore && page > 1) return;
    try {
      const response = await api.get(`/hybrid-feed/?page=${page}`);
      const data = response.data;
      setProperties(prev => page === 1 ? data : [...prev, ...data]);
      setHasMore(data.length === 10); 
      if (page === 1) setLoading(false);
    } catch (err) {
      console.error('Feed load failed:', err);
      if (page === 1) {
        setProperties([]);
        setLoading(false);
      }
      setHasMore(false);
    }
  }, [hasMore]);

  // INITIAL LOAD
  useEffect(() => {
    fetchFeedPage(1);
  }, [fetchFeedPage]);

  // SCROLL LISTENER FOR PAGINATION
  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef.current;
      if (!container || !hasMore || loading) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop <= clientHeight + 800) {
        setCurrentPage(p => p + 1);
      }
    };

    const container = containerRef.current;
    container?.addEventListener('scroll', handleScroll, { passive: true });
    return () => container?.removeEventListener('scroll', handleScroll);
  }, [hasMore, loading]);

  // LOAD NEXT PAGE WHEN currentPage CHANGES
  useEffect(() => {
    if (currentPage > 1) {
      fetchFeedPage(currentPage);
    }
  }, [currentPage, fetchFeedPage]);



  // Optimized scroll handler with throttling
  const handleScroll = useCallback((e) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    setIsScrolling(true);
    
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 150);

    const container = e.currentTarget;
    const scrollPosition = container.scrollTop;
    const itemHeight = window.innerHeight;
    const newIndex = Math.round(scrollPosition / itemHeight);
    
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < properties.length) {
      setCurrentIndex(newIndex);
    }
  }, [currentIndex, properties.length]);

  // Optimized like handler with per‑post busy state
  const handleLike = async (propertyId, currentLiked) => {
    if (likeBusyIds.has(propertyId)) return; // prevent double‑click spam

    setLikeBusyIds(prev => {
      const copy = new Set(prev);
      copy.add(propertyId);
      return copy;
    });

    // optimistic UI update
    setProperties(prev =>
      prev.map(p =>
        p.id === propertyId
          ? {
              ...p,
              is_liked: !currentLiked,
              likes_count: p.likes_count + (currentLiked ? -1 : 1),
            }
          : p
      )
    );

    try {
      await api.post(`/properties/${propertyId}/like/`);
    } catch (err) {
      // rollback on failure
      setProperties(prev =>
        prev.map(p =>
          p.id === propertyId
            ? {
                ...p,
                is_liked: currentLiked,
                likes_count: p.likes_count + (currentLiked ? 1 : -1),
              }
            : p
        )
      );
      console.error('Failed to like property:', err);
    } finally {
      setLikeBusyIds(prev => {
        const copy = new Set(prev);
        copy.delete(propertyId);
        return copy;
      });
    }
  };


  const handleShare = (property) => {
    setShareModal({ isOpen: true, property });
  };

  const openComments = async (propertyId) => {
    setCommentBusyId(propertyId);
    try {
      const res = await api.get(`/properties/${propertyId}/comments/`);
      setCommentPanel({ isOpen: true, propertyId, comments: res.data });
    } catch (err) {
      console.error('Failed to load comments:', err);
      setCommentPanel({ isOpen: true, propertyId, comments: [] });
    } finally {
      setCommentBusyId(null);
    }
  };

  
  const handleCardClick = (property, index) => {
    setSelectedProperty(property);
    setSelectedIndex(index);
    setIsModalOpen(true);
    prefetchRelated(property.id);
  };

  const prefetchRelated = useCallback(async (propertyId) => {
    try {
      await api.get(`/properties/${propertyId}/related/?limit=6`);
    } catch (err) {
    }
  }, []);


  // Image preloading optimization
  const handleImageLoad = useCallback((propertyId) => {
    setLoadedImages(prev => new Set(prev).add(propertyId));
  }, []);




  // Preload adjacent images for smoother experience
  useEffect(() => {
    const preloadImages = () => {
      const indicesToPreload = [
        Math.max(0, currentIndex - 1),
        currentIndex,
        Math.min(properties.length - 1, currentIndex + 1),
        Math.min(properties.length - 1, currentIndex + 2)
      ];

      indicesToPreload.forEach(index => {
        const property = properties[index];
        if (property && !loadedImages.has(property.id)) {
          const img = new Image();
          img.src = property.images[0]?.image || '/fallback.jpg';
          img.onload = () => handleImageLoad(property.id);
        }
      });
    };

    preloadImages();
  }, [currentIndex, loadedImages, properties, handleImageLoad]);

  // Scroll event listener with cleanup
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        container.removeEventListener('scroll', handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }
  }, [handleScroll]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowUp' && currentIndex > 0) {
        const container = containerRef.current;
        if (container) {
          container.scrollTo({
            top: (currentIndex - 1) * window.innerHeight,
            behavior: 'smooth'
          });
        }
      } else if (e.key === 'ArrowDown' && currentIndex < properties.length - 1) {
        const container = containerRef.current;
        if (container) {
          container.scrollTo({
            top: (currentIndex + 1) * window.innerHeight,
            behavior: 'smooth'
          });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, properties.length]);

useEffect(() => {
  const urlParams = new URLSearchParams(location.search);
  const propertyIdParam = urlParams.get('property_id');

  if (!propertyIdParam || propertyIdParam === 'undefined') {
    return;
  }

  const idx = properties.findIndex(
      p => String(p.id) === String(propertyIdParam)
    );
    
    if (idx >= 0) {
      setSelectedProperty(properties[idx]);
      setSelectedIndex(idx);
      setIsModalOpen(true);
      navigate('/homedash', { replace: true });
    } else if (properties.length > 0) {
      const fetchProperty = async () => {
        try {
          const res = await api.get(`/pongo/properties/${propertyIdParam}/`);
          setSelectedProperty(res.data);
          setIsModalOpen(true);
          navigate('/homedash', { replace: true });
        } catch (err) {
          console.warn('Property not found:', err);
        }
      };
      fetchProperty();
    }
  }, [properties, location.search, navigate]);


  useEffect(() => {
    const fetchFxRates = async () => {
        try {
          const fxRes = await api.get('/fx-rates/');
          setFxRates(fxRes.data);
          const savedCurrency = localStorage.getItem('preferredCurrency') || 'RWF';
          setUserCurrency(savedCurrency);
        } catch (err) {
          console.error('Failed to fetch FX rates:', err);
        }
      };
      fetchFxRates();
  }, []);

  // Add this useEffect for debugging
useEffect(() => {
  const debugFxRates = async () => {
    try {
      const response = await api.get('/fx-rates/');
      const data = response.data;

      console.log('FX Rates:',  {
        status: response.status,
        data: Object.keys(data).length > 0 ? Object.keys(data) : 'empty',
        sample: Object.entries(data).slice(0, 3)
      });
      
      // Check if we have real API rates or fallback
      const isFallback = !data['EUR'] || Math.abs(data['EUR'] - 0.93) < 0.01;
      console.log(`Currency source: ${isFallback ? 'FALLBACK (hardcoded)' : 'REAL API (ExchangeRate)'}`);
      
    } catch (err) {
      console.error('FX Rates debug failed:', err);
    }
  };
  
  debugFxRates();
}, []);


  if (loading) return <LoadingSpinner />;
  if (properties.length === 0) return <div className="text-white text-center py-20">{t('home.noProperties')}</div>;


  
  return (
    <div className="flex h-screen bg-[var(--bg-black)]  overflow-hidden">
      {/* Main Content */}
      <div className="flex-1 transition-all duration-300">
        {/* Property Feed */}
        <div
          ref={containerRef}
          className="h-screen pt-14 sm:pt-16 overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
          style={{ 
            scrollSnapType: 'y mandatory',
            scrollBehavior: 'smooth'
          }}
        >
          {properties.map((property, index) => (
            <div
              key={property.id}
              className="h-screen  items-center justify-center px-2 py-4 sm:px-4 sm:py-6 md:px-6 md:py-8 lg:px-8 lg:py-10"
              style={{ scrollSnapAlign: 'start' }}
            >
              {/* Main Container -  Layout */}
              <div className="relative flex items-center justify-center w-full max-w-6xl h-full">                
                <PropertyCard
                  property={property}
                  isActive={index === currentIndex}
                  isScrolling={isScrolling}
                  loading={index <= 1 ? "eager" : "lazy"}
                  onImageLoad={handleImageLoad}
                  onClick={() => handleCardClick(property, index)}
                  fxRates={fxRates}
                  userCurrency={userCurrency}
                />
                
                {/* Right Side Outside Card */}
                <div className="absolute right-2 sm:right-4 md:right-6 lg:right-12 bottom-19 sm:bottom-20 flex flex-col gap-4 sm:gap-6 z-10">
                  {/* Profile Picture with Follow Button */}
                  <div className="flex flex-col rounded-full items-center sm:mr-6 md:mr-16 lg:mr-16">
                    <div className="relative">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full overflow-hidden border-2 sm:border-3 border-white shadow-lg">
                        {(() => {
                          const displayName = property.owner?.username || 'U';
                          const initials = displayName.charAt(0).toUpperCase();
                          const bgColor = stringToColor(property.owner?.username || property.owner?.id || 'user').substring(1);
                          const avatarUrl = property.owner?.avatar || 
                            `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=${bgColor}&color=fff&size=128`;
                          return (
                            <OptimizedImage
                              src={avatarUrl}
                              alt={property.owner?.username || 'User'}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              t={t}
                            />
                          );
                        })()}
                      </div>
                      <Link
                        to={`/user/${property.owner.id}`}
                        className="absolute border border-none -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 sm:w-7 sm:h-7 bg-pink-500 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold hover:bg-pink-600 transition-all duration-200 hover:scale-110"
                        onClick={(e) => e.stopPropagation()}
                      >
                        +
                      </Link>
                    </div>
                  </div>

                  {/* Action Buttons with Counts */}
                  <div className="flex flex-col gap-4 -mb-10 sm:mr-6 md:mr-16 lg:mr-16">
                    {/* Like */}
                    <div className="flex flex-col items-center group">
                      <button
                        disabled={likeBusyIds.has(property.id)}
                        className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full border border-none flex items-center justify-center transition-all duration-300 shadow-lg hover:shadow-xl ${
                          property.is_liked
                            ? 'bg-gray-800 text-white scale-110'
                            : 'bg-white/10 backdrop-blur-sm text-white hover:bg-red-500 hover:scale-105'
                        } ${likeBusyIds.has(property.id) ? 'opacity-60 cursor-not-allowed' : ''}`}
                        onClick={() => handleLike(property.id, property.is_liked)}
                        aria-label={t('home.actions.like')}
                      >
                        {likeBusyIds.has(property.id) ? (
                          <div className="w-4 h-4 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <FaHeart
                            className={property.is_liked ? 'text-red-500 fill-current' : 'text-[var(--text-black)]'}
                          />
                        )}
                      </button>
                      {property.likes_count > 0 && (
                        <span className="text-[var(--text-black)] text-xs font-medium mt-1">
                          {property.likes_count}
                        </span>
                      )}
                    </div>

                    {/* Comment */}
                    <div className="flex flex-col items-center group">
                      <button
                        onClick={() => openComments(property.id)}
                        disabled={commentBusyId === property.id}
                        className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full border border-none flex items-center justify-center bg-white/10 backdrop-blur-sm text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 ${
                          commentBusyId === property.id ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                        aria-label={t('home.actions.comment')}
                      >
                        {commentBusyId === property.id ? (
                          <div className="w-4 h-4 border-2 border-pink-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <FaComment className="text-[var(--text-black)]" size={16} />
                        )}
                      </button>
                      {property.comments_count > 0 && (
                        <span className="text-[var(--text-black)] text-xs font-medium mt-1">
                          {property.comments_count}
                        </span>
                      )}
                    </div>


                    {/* Share (optional: add shares_count later) */}
                    <div className="flex flex-col items-center group">
                      <button
                        onClick={() => handleShare(property)}
                        className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full border border-none flex items-center justify-center bg-white/10 backdrop-blur-sm text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                        aria-label={t('home.actions.share')}
                      >
                        <FaShare className="text-[var(--text-black)]" size={16} />
                      </button>
                      {/* Uncomment when shares_count is added */}
                      {property.shares_count > 0 && (
                        <span className="text-[var(--text-black)] text-xs font-medium mt-1">
                          {property.shares_count}
                        </span>
                      )}
                    </div>

                    {/* Message*/}
                    <div className="flex flex-col items-center group">
                      <button
                        onClick={() => {
                          navigate(`/messages?property_id=${property.id}`);
                        }}
                        className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-full border border-none flex items-center justify-center bg-white/10 backdrop-blur-sm text-white hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                        aria-label={t('home.actions.message')}
                      >
                        <FaPaperPlane className="text-[var(--text-black)]" size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <CommentPanel
        isOpen={commentPanel.isOpen}
        onClose={() => setCommentPanel({ isOpen: false, propertyId: null, comments: [] })}
        propertyId={commentPanel.propertyId}
        comments={commentPanel.comments}
        onCommentPosted={(newComment) => {
          setCommentPanel(prev => ({
            ...prev,
            comments: [newComment, ...prev.comments]
          }));
          // Optional: Also update property's comment count locally
          setProperties(prev =>
            prev.map(p =>
              p.id === commentPanel.propertyId
                ? { ...p, comments_count: p.comments_count + 1 }
                : p
            )
          );
        }}
      />

      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={() => setShareModal({ isOpen: false, property: null })}
        property={shareModal.property}
        onShareSuccess={() => {
          // Optional: update shares_count locally if you add it later
        }}
      />

      <PropertyModal
        property={selectedProperty}
        properties={properties}
        initialIndex={selectedIndex}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onMessageOwner={(prop) => {
          navigate(`/messages?property_id=${prop.id}`);
        }}
      />

      {/* Custom Styles */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        /* Performance optimizations */
        .snap-y {
          scroll-behavior: smooth;
        }
        
        /* Reduce animations for users who prefer reduced motion */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
          .snap-y {
            scroll-behavior: auto;
          }
        }
        
        /* Hardware acceleration for smooth animations */
        .transform {
          transform: translateZ(0);
        }
        
        /* Optimize for touch devices */
        @media (hover: none) and (pointer: coarse) {
          .hover\\:scale-105:hover {
            transform: scale(1);
          }
          .hover\\:scale-110:hover {
            transform: scale(1);
          }
          .hover\\:scale-\\[1\\.02\\]:hover {
            transform: scale(1);
          }
        }

        .comment-panel-enter {
          transform: translateX(100%);
        }
        .comment-panel-enter-active {
          transform: translateX(0%);
          transition: transform 300ms ease-out;
        }
        .comment-panel-exit {
          transform: translateX(0%);
        }
        .comment-panel-exit-active {
          transform: translateX(100%);
          transition: transform 300ms ease-in;
        }
      `}</style>
    
      {/* Currency & Language Selectors */}
      {!commentPanel.isOpen && (
        <div className="fixed top-4 right-4 z-50 flex gap-2">
          <CurrencySelector 
            userCurrency={userCurrency} 
            setUserCurrency={setUserCurrency} 
            fxRates={fxRates} 
            localStorage={localStorage} 
          />

          <LanguageSelector 
            i18n={i18n} 
            localStorage={localStorage} 
          />
        </div>
      )}
    </div>
  );
}